import { api } from "@/domains/shared/services/api";
import { ensureCsrfToken } from "@/domains/shared/services/setupCsrfInterceptor";
import type { CancelTrialResponse } from "@/domains/subscription/billingHelpers";
import type { PlanChangeSecretaryCandidates } from "@/domains/subscription/planChangeSecretaryCandidates";
import type { SubscriptionPlanId } from "@/domains/subscription/plans";
import type {
    AttendanceChangePreview,
    ClinicCareAccessResponse,
    ClinicInvitePreview,
    ClinicMarketplaceMeResponse,
    ClinicMembersResponse,
    ClinicSetupPayload,
    ClinicSetupResponse,
    ClinicSubscriptionResponse,
    ClinicCancelSubscriptionResponse,
    ClinicPlanFitResponse,
    ClinicUnitAssignmentItem,
    ClinicUnitListItem,
    RegisterClinicPayload,
} from "@/domains/multi/clinicTypes";
import type { WorkScheduleResponse, WorkScheduleSlot } from "@/domains/multi/workSchedule";
import type { IUser } from "@/domains/shared/models/IUser";
import type { RegistrationConsentState } from "@/components/auth/RegistrationConsentBlock";
import { buildRegistrationLocalePayload } from "@mivo/i18n";

const clinicsUrl = "/multi/me";

export const clinicApi = {
    setupClinic: async (payload: ClinicSetupPayload) =>
        api.post<ClinicSetupResponse>("/multi/setup", payload),

    getClinic: async () => api.get(clinicsUrl),
    patchClinic: async (payload: { name?: string; phone?: string | null; address?: string | null }) =>
        api.patch<{ id: string; name: string; phone: string | null; address: string | null }>(clinicsUrl, payload),
    listMembers: async () => api.get<ClinicMembersResponse>(`${clinicsUrl}/members`),
    listUnits: async () => api.get<ClinicUnitListItem[]>(`${clinicsUrl}/units`),
    listUnitsForProfessional: async (userId: string) =>
        api.get<ClinicUnitListItem[]>(`${clinicsUrl}/units/for-professional`, {
            params: { userId },
        }),
    createUnit: async (name: string) =>
        api.post<ClinicUnitListItem>(`${clinicsUrl}/units`, { name }),
    renameUnit: async (unitId: string, name: string) =>
        api.patch<ClinicUnitListItem>(
            `${clinicsUrl}/units/${encodeURIComponent(unitId)}`,
            { name },
        ),
    deactivateUnit: async (unitId: string) =>
        api.post<{ ok: boolean }>(`${clinicsUrl}/units/${encodeURIComponent(unitId)}/deactivate`),
    activateUnit: async (unitId: string) =>
        api.post<{ ok: boolean }>(`${clinicsUrl}/units/${encodeURIComponent(unitId)}/activate`),
    listUnitAssignments: async (unitId: string) =>
        api.get<ClinicUnitAssignmentItem[]>(
            `${clinicsUrl}/units/${encodeURIComponent(unitId)}/assignments`,
        ),
    putUnitAssignments: async (unitId: string, membershipIds: string[]) =>
        api.put<ClinicUnitAssignmentItem[]>(
            `${clinicsUrl}/units/${encodeURIComponent(unitId)}/assignments`,
            { membershipIds },
        ),
    getAttendanceUnit: async (patientId: string) =>
        api.get<{
            patientId: string;
            attendanceUnitId: string | null;
            attendanceUnitName: string | null;
        }>(`${clinicsUrl}/patients/${encodeURIComponent(patientId)}/attendance-unit`),
    previewAttendanceUnit: async (patientId: string, unitId: string) =>
        api.post<AttendanceChangePreview>(
            `${clinicsUrl}/patients/${encodeURIComponent(patientId)}/attendance-unit/preview`,
            { unitId },
        ),
    changeAttendanceUnit: async (patientId: string, unitId: string, therapistId?: string) =>
        api.post<{ ok: boolean; attendanceUnitId: string }>(
            `${clinicsUrl}/patients/${encodeURIComponent(patientId)}/attendance-unit`,
            { unitId, therapistId, confirm: true },
        ),
    createInvite: async (payload: {
        email: string;
        inviteeName: string;
        targetRoles: string[];
        expectedCpf: string;
        expectedSpecialty?: string;
        expectedCouncilNumber?: string;
    }) => api.post(`${clinicsUrl}/members/invites`, payload),
    resendInvite: async (inviteId: string) =>
        api.post(`${clinicsUrl}/members/invites/${encodeURIComponent(inviteId)}/resend`),
    revokeInvite: async (inviteId: string) =>
        api.post(`${clinicsUrl}/members/invites/${encodeURIComponent(inviteId)}/revoke`),
    deactivateMember: async (membershipId: string) =>
        api.post(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/deactivate`),
    reactivateMember: async (membershipId: string) =>
        api.post(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/reactivate`),
    patchMemberRoles: async (
        membershipId: string,
        payload: { roles: string[]; specialty?: string; councilNumber?: string },
    ) => api.patch(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/roles`, payload),
    reassignPrimaryPatients: async (membershipId: string, newPrimaryTherapistUserId: string) =>
        api.post(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/reassign-primary-patients`, {
            newPrimaryTherapistUserId,
        }),
    reassignPrimaryPatientsAndDeactivate: async (membershipId: string, newPrimaryTherapistUserId: string) =>
        api.post(
            `${clinicsUrl}/members/${encodeURIComponent(membershipId)}/reassign-primary-patients-and-deactivate`,
            { newPrimaryTherapistUserId },
        ),
    getCareAccess: async (patientId: string) =>
        api.get<ClinicCareAccessResponse>(`/patients/${encodeURIComponent(patientId)}/care-access`),
    grantCareAccess: async (patientId: string, professionalUserId: string) =>
        api.post<ClinicCareAccessResponse>(`/patients/${encodeURIComponent(patientId)}/care-access`, {
            professionalUserId,
        }),
    revokeCareAccess: async (patientId: string, professionalUserId: string) =>
        api.delete(`/patients/${encodeURIComponent(patientId)}/care-access/${encodeURIComponent(professionalUserId)}`),
    listMarketplaceModules: async () => api.get(`${clinicsUrl}/marketplace/modules`),
    getMarketplaceMe: async () => api.get<ClinicMarketplaceMeResponse>(`${clinicsUrl}/marketplace/me`),
    checkoutMarketplaceModule: async (moduleSlug: string) =>
        api.post(`${clinicsUrl}/marketplace/${encodeURIComponent(moduleSlug)}/checkout`, {}),
    checkoutMarketplaceModuleCreditCard: async (
        moduleSlug: string,
        payload: {
            card: {
                holderName: string;
                number: string;
                expiryMonth: string;
                expiryYear: string;
                ccv: string;
            };
            holder: {
                name: string;
                email: string;
                cpfCnpj: string;
                postalCode: string;
                addressNumber: string;
                addressComplement?: string | null;
                phone?: string | null;
                mobilePhone?: string | null;
            };
        },
    ) => api.post(`${clinicsUrl}/marketplace/${encodeURIComponent(moduleSlug)}/checkout`, payload),
    cancelMarketplaceModule: async (moduleSlug: string) =>
        api.post(`${clinicsUrl}/marketplace/${encodeURIComponent(moduleSlug)}/subscription/cancel`, {}),
    reactivateMarketplaceModule: async (moduleSlug: string) =>
        api.post(`${clinicsUrl}/marketplace/${encodeURIComponent(moduleSlug)}/subscription/reactivate`, {}),
    getSubscription: async () => api.get<ClinicSubscriptionResponse>(`${clinicsUrl}/billing/subscription`),
    /** Read-only Multi→Básico/Pro fit check for Continuar / Trocar (BR-CCF-10). */
    checkPlanFit: async (payload: { targetPlanId: string; targetPlanVersion?: number | null }) =>
        api.post<ClinicPlanFitResponse>(`${clinicsUrl}/billing/subscription/plan-fit`, payload),
    patchSeats: async (payload: {
        extraProfessionals: number;
        extraSecretaries: number;
        extraFinanceUsers: number;
    }) => api.patch(`${clinicsUrl}/billing/seats`, payload),
    cancelSubscription: async () =>
        api.post<ClinicCancelSubscriptionResponse>(`${clinicsUrl}/billing/subscription/cancel`, {}),
    reactivateSubscription: async () => api.post(`${clinicsUrl}/billing/subscription/reactivate`, {}),
    checkoutSubscription: async (payload?: { planSlug?: string; planVersionId?: string | null }) =>
        api.post(`${clinicsUrl}/billing/subscription/checkout`, payload ?? {}),
    fetchChangePlanSecretaryCandidates: async () =>
        api.get<PlanChangeSecretaryCandidates>(`${clinicsUrl}/billing/subscription/change-plan/secretary-candidates`),
    changeSubscriptionPlan: async (payload: { planId: SubscriptionPlanId; selectedSecretaryUserId?: string | null }) => {
        const idempotencyKey = crypto.randomUUID();
        return api.post<{
            planId: string;
            scheduledForNextCycle: boolean;
            effectiveAt?: string | null;
            proratedChargeBrl?: number | null;
            reactivated?: boolean;
        }>(`${clinicsUrl}/billing/subscription/change-plan`, payload, {
            headers: { "Idempotency-Key": idempotencyKey },
        });
    },
    checkoutSubscriptionPix: async (payload?: { planSlug?: string; planVersionId?: string | null }) => {
        await ensureCsrfToken();
        return api.post<{
            sessionId: string;
            qrPayload: string;
            status: string;
            devSimulatePixAvailable?: boolean;
        }>(`${clinicsUrl}/billing/subscription/checkout/pix`, payload ?? {});
    },
    pollSubscriptionPixCheckout: async (sessionId: string) =>
        api.get<{
            sessionId: string;
            isPaid: boolean;
            planId?: string;
            authorizationStatus?: string;
            devSimulatePixAvailable?: boolean;
        }>(`${clinicsUrl}/billing/subscription/checkout/pix/${encodeURIComponent(sessionId)}`),
    simulateSubscriptionPixCheckout: async (sessionId: string) =>
        api.post<{ sessionId: string; isPaid: boolean; planId?: string }>(
            `${clinicsUrl}/billing/subscription/checkout/pix/${encodeURIComponent(sessionId)}/simulate`,
        ),
    changeSubscriptionPaymentMethod: async (payload: {
        paymentMethod: "credit_card" | "pix_automatic";
        planId?: SubscriptionPlanId;
        card?: {
            holderName: string;
            number: string;
            expiryMonth: string;
            expiryYear: string;
            ccv: string;
        };
        holder?: {
            name: string;
            email: string;
            cpfCnpj: string;
            postalCode: string;
            addressNumber: string;
            addressComplement?: string | null;
            phone?: string | null;
            mobilePhone?: string | null;
        };
    }) => api.post(`${clinicsUrl}/billing/subscription/change-payment-method`, payload),
    cancelTrialSubscription: async (acknowledgedWarnings?: boolean) =>
        api.post<CancelTrialResponse>(`${clinicsUrl}/billing/subscription/cancel-trial`, {
            acknowledgedWarnings: acknowledgedWarnings ?? false,
        }),
    simulateTrialChargeCheckout: async () =>
        api.post<{
            charged: boolean;
            planId: string;
            subscriptionEndsAt: string;
        }>(`${clinicsUrl}/billing/subscription/checkout/simulate-trial-charge`),
    checkoutSubscriptionCreditCard: async (payload: {
        planSlug?: string;
        planVersionId?: string | null;
        card: {
            holderName: string;
            number: string;
            expiryMonth: string;
            expiryYear: string;
            ccv: string;
        };
        holder: {
            name: string;
            email: string;
            cpfCnpj: string;
            postalCode: string;
            addressNumber: string;
            addressComplement?: string | null;
            phone?: string | null;
            mobilePhone?: string | null;
        };
    }) => api.post(`${clinicsUrl}/billing/subscription/checkout`, { paymentMethod: "credit_card", ...payload }),
    putActingProfessional: async (professionalUserId: string) =>
        api.put(`${clinicsUrl}/acting-professional`, { professionalUserId }),
    listTeams: async () => api.get(`${clinicsUrl}/teams`),
    approveLeaveRequest: async (membershipId: string) =>
        api.post(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/leave-request/approve`),
    denyLeaveRequest: async (membershipId: string) =>
        api.post(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/leave-request/deny`),
    transferMainManager: async (successorMembershipId: string) =>
        api.post(`${clinicsUrl}/members/transfer-main-manager`, { successorMembershipId }),
    getWorkSchedule: async (membershipId: string) =>
        api.get<WorkScheduleResponse>(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/work-schedule`),
    proposeWorkSchedule: async (membershipId: string, slots: WorkScheduleSlot[]) =>
        api.post<WorkScheduleResponse>(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/work-schedule/propose`, {
            slots,
        }),
    approveMemberWorkSchedule: async (membershipId: string) =>
        api.post<{ ok: boolean }>(`${clinicsUrl}/members/${encodeURIComponent(membershipId)}/work-schedule/approve`),

    /** GET orphans API; patient list UI filters GET /patients instead of calling this. */
    listOrphanPatients: async (q?: string) =>
        api.get<{ items: Array<{ id: string; name: string; primaryTherapistId: null; clinicId: string }> }>(
            `${clinicsUrl}/patients/orphans`,
            { params: q?.trim() ? { q: q.trim() } : undefined },
        ),
    assignOrphanPrimary: async (patientId: string, therapistUserId: string) =>
        api.post<{ patientId: string; primaryTherapistId: string; ok: boolean }>(
            `${clinicsUrl}/patients/${encodeURIComponent(patientId)}/assign-primary`,
            { therapistUserId },
        ),
};

export const clinicAuthApi = {
    registerClinic: async (payload: RegisterClinicPayload) => {
        const prefs = buildRegistrationLocalePayload();
        return api.post<{ user: IUser; clinicId: string; accessToken: string }>("/auth/register-multi", {
            ...payload,
            preferredLocale: prefs.preferredLocale,
            marketCountry: prefs.marketCountry,
            legalConsents: {
                ...payload.legalConsents,
                acceptedLocale: prefs.acceptedLocale,
            },
        });
    },

    getClinicInvitePreview: async (token: string) =>
        api.get<ClinicInvitePreview>(`/auth/clinic-invite/${encodeURIComponent(token)}`),

    acceptClinicInvite: async (
        token: string,
        name: string,
        password: string | undefined,
        consent: RegistrationConsentState,
        specialty?: string,
        councilNumber?: string,
    ) => {
        const prefs = buildRegistrationLocalePayload();
        return api.post<{ user: IUser; accessToken: string }>("/auth/accept-clinic-invite", {
            token,
            name,
            password,
            specialty,
            councilNumber,
            preferredLocale: prefs.preferredLocale,
            marketCountry: prefs.marketCountry,
            legalConsents: {
                terms: consent.acceptedTerms,
                privacy: consent.acceptedPrivacy,
                acceptedLocale: prefs.acceptedLocale,
            },
        });
    },

    registerClinicSecretary: async (
        token: string,
        name: string,
        password: string,
        consent: RegistrationConsentState,
    ) => {
        const prefs = buildRegistrationLocalePayload();
        return api.post<{ user: IUser; accessToken: string }>("/auth/register-clinic-secretary", {
            token,
            name,
            password,
            preferredLocale: prefs.preferredLocale,
            marketCountry: prefs.marketCountry,
            legalConsents: {
                terms: consent.acceptedTerms,
                privacy: consent.acceptedPrivacy,
                acceptedLocale: prefs.acceptedLocale,
            },
        });
    },

    setActiveClinic: async (clinicId: string) =>
        api.put<{ user: IUser; accessToken: string; activeClinicId: string }>("/auth/me/active-clinic", { clinicId }),

    requestClinicLeave: async (reason?: string) =>
        api.post<{ leaveRequestStatus: string; user: IUser }>("/auth/me/clinic/leave-request", { reason }),

    cancelClinicLeave: async () =>
        api.delete<{ leaveRequestStatus: string; user: IUser }>("/auth/me/clinic/leave-request"),

    approveWorkSchedule: async () => api.post<{ ok: boolean; user: IUser }>("/auth/me/clinic/work-schedule/approve"),

    proposeMyWorkSchedule: async (slots: WorkScheduleSlot[]) =>
        api.post<WorkScheduleResponse & { user: IUser }>("/auth/me/clinic/work-schedule/propose", { slots }),

};
